import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useFamily } from '../../contexts/FamilyContext'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import { getRandomRewards, getAgeAppropriateRewards } from '../../data/rewardTemplates'
import toast from 'react-hot-toast'

const { FiPlus, FiRefreshCw, FiGift, FiZap } = FiIcons

const QuickRewardSuggestions = ({ maxSuggestions = 6 }) => {
  const { createReward } = useFamily()
  const { family } = useAuth()
  const [loading, setLoading] = useState(false)
  const [suggestions, setSuggestions] = useState(() => {
    // Get age-appropriate suggestions based on family children
    if (family?.children && family.children.length > 0) {
      const averageAge = family.children.reduce((sum, child) => sum + (child.age || 8), 0) / family.children.length
      return getAgeAppropriateRewards(averageAge).slice(0, maxSuggestions)
    }
    return getRandomRewards('food', maxSuggestions)
  })

  const refreshSuggestions = () => {
    const categories = ['food', 'screenTime', 'activities', 'social', 'privileges', 'creative']
    const randomCategory = categories[Math.floor(Math.random() * categories.length)]
    const newSuggestions = getRandomRewards(randomCategory, maxSuggestions)
    setSuggestions(newSuggestions)
    toast.success('Fresh reward suggestions loaded! 🔄')
  }

  const handleAddReward = async (reward) => {
    setLoading(true)
    
    try {
      const rewardData = {
        title: reward.title,
        description: reward.description,
        points_required: reward.points,
        emoji: reward.emoji
      }

      const { error } = await createReward(rewardData)
      
      if (error) {
        console.error('Error creating reward:', error)
        toast.error('Failed to add reward')
      } else {
        toast.success(`${reward.title} added successfully! 🎉`)
        // Remove the added reward from suggestions
        setSuggestions(prev => prev.filter(r => r.title !== reward.title))
      }
    } catch (error) {
      console.error('Unexpected error:', error)
      toast.error('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  const getPointRangeColor = (points) => {
    if (points >= 150) return 'text-purple-500'
    if (points >= 80) return 'text-red-500'
    if (points >= 40) return 'text-orange-500'
    return 'text-green-500'
  }

  const getPointRangeLabel = (points) => {
    if (points >= 150) return 'Mega'
    if (points >= 80) return 'Big'
    if (points >= 40) return 'Medium'
    return 'Quick'
  }

  if (suggestions.length === 0) {
    return (
      <Card className="p-6 text-center">
        <div className="text-4xl mb-3">✨</div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          All suggestions added!
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Get fresh reward ideas for your family
        </p>
        <Button variant="primary" onClick={refreshSuggestions}>
          <SafeIcon icon={FiRefreshCw} />
          Get New Suggestions
        </Button>
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Quick Reward Suggestions
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Popular rewards perfect for motivating your kids
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refreshSuggestions}
          className="flex items-center gap-2"
        >
          <SafeIcon icon={FiRefreshCw} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {suggestions.map((reward, index) => (
          <motion.div
            key={reward.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
          >
            <Card className="p-4 hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700">
              <div className="flex items-start gap-3 mb-3">
                <div className="text-2xl">{reward.emoji}</div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {reward.title}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {reward.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <Badge variant="primary" size="sm" className="flex items-center gap-1">
                  <SafeIcon icon={FiGift} className={`text-xs ${getPointRangeColor(reward.points)}`} />
                  {reward.points} pts
                </Badge>
                <Badge variant="secondary" size="sm">
                  {getPointRangeLabel(reward.points)}
                </Badge>
              </div>

              <Button
                variant="primary"
                size="sm"
                className="w-full"
                onClick={() => handleAddReward(reward)}
                disabled={loading}
              >
                <SafeIcon icon={FiPlus} className="text-sm" />
                Add Reward
              </Button>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <SafeIcon icon={FiZap} className="text-primary-600 dark:text-primary-400" />
          <span className="font-medium text-primary-800 dark:text-primary-300">Pro Tip</span>
        </div>
        <p className="text-sm text-primary-700 dark:text-primary-400">
          These reward suggestions are tailored based on your family's children ages and popular reward categories. 
          Click "Refresh" to see different options, or use the full Reward Library for 100+ more choices!
        </p>
      </div>
    </Card>
  )
}

export default QuickRewardSuggestions